/**
 * bw-theme.js
 * Behaviour for the Burke Williams design-system components.
 *
 * Deliberately self-contained and delegated from document, so it cannot
 * collide with the existing theme.js / vendor.js bundles. No dependencies.
 */
(function () {
  'use strict';

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    // --- Mobile navigation toggle -------------------------------------
    var toggle = target.closest('[data-bw-nav-toggle]');
    if (toggle) {
      var header = toggle.closest('[data-bw-header]');
      if (!header) return;
      var panel = header.querySelector('[data-bw-nav-panel]');
      if (!panel) return;

      var isOpen = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      panel.hidden = isOpen;
      header.classList.toggle('bw-header--open', !isOpen);
      return;
    }

    // Wishlist is owned by the wishlist app. The theme deliberately binds no
    // handler: it only renders the button and styles the selected state off
    // aria-pressed, so the app can attach to [data-bw-wishlist] and drive it.
  });

  // Close the mobile panel on Escape.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var open = document.querySelector('[data-bw-nav-toggle][aria-expanded="true"]');
    if (!open) return;
    var header = open.closest('[data-bw-header]');
    var panel = header && header.querySelector('[data-bw-nav-panel]');
    open.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;
    if (header) header.classList.remove('bw-header--open');
  });
})();

/**
 * Collection filter behaviour.
 * Kept in its own IIFE so a failure here cannot take out the header.
 */
(function () {
  'use strict';

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    // Collapse / expand a filter group.
    var toggle = target.closest('[data-bw-filter-toggle]');
    if (toggle) {
      var group = toggle.closest('[data-bw-filter-group]');
      if (!group) return;
      var body = group.querySelector('[data-bw-filter-body]');
      var expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      if (body) body.hidden = expanded;
      return;
    }

    // Reveal filter values beyond the first six.
    var showAll = target.closest('[data-bw-filter-show-all]');
    if (showAll) {
      var container = showAll.closest('[data-bw-filter-body]');
      if (!container) return;
      var isOpen = showAll.getAttribute('aria-expanded') === 'true';
      var overflow = container.querySelectorAll('[data-bw-filter-overflow]');
      for (var i = 0; i < overflow.length; i++) {
        overflow[i].classList.toggle('is-revealed', !isOpen);
      }
      showAll.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    }
  });

  // Applying filters is owned by the Section Rendering layer further down,
  // which swaps results in place instead of reloading. It falls back to a
  // real navigation if that is unavailable.
})();

/**
 * Product detail behaviour: gallery, quantity stepper, accordions, and
 * keeping the subscribe radio in sync with the frequency select.
 */
(function () {
  'use strict';

  function galleryImages(gallery) {
    return Array.prototype.slice.call(gallery.querySelectorAll('[data-bw-thumb]'));
  }

  function showImage(gallery, index) {
    var thumbs = galleryImages(gallery);
    if (!thumbs.length) return;
    if (index < 0) index = thumbs.length - 1;
    if (index >= thumbs.length) index = 0;

    var main = gallery.querySelector('[data-bw-gallery-main]');
    var thumb = thumbs[index];
    if (main && thumb) {
      var full = thumb.getAttribute('data-full');
      if (full) main.setAttribute('src', full);
      var img = thumb.querySelector('img');
      if (img) main.setAttribute('alt', img.getAttribute('alt') || '');
    }
    for (var i = 0; i < thumbs.length; i++) {
      thumbs[i].classList.toggle('is-active', i === index);
    }
    gallery.setAttribute('data-current', String(index));
  }

  function currentIndex(gallery) {
    var v = parseInt(gallery.getAttribute('data-current'), 10);
    return isNaN(v) ? 0 : v;
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    // --- Gallery ---
    var thumb = target.closest('[data-bw-thumb]');
    if (thumb) {
      var g = thumb.closest('[data-bw-gallery]');
      if (g) showImage(g, parseInt(thumb.getAttribute('data-image-index'), 10) || 0);
      return;
    }

    var prev = target.closest('[data-bw-gallery-prev]');
    if (prev) {
      var gp = prev.closest('[data-bw-gallery]');
      if (gp) showImage(gp, currentIndex(gp) - 1);
      return;
    }

    var next = target.closest('[data-bw-gallery-next]');
    if (next) {
      var gn = next.closest('[data-bw-gallery]');
      if (gn) showImage(gn, currentIndex(gn) + 1);
      return;
    }

    // --- Quantity stepper ---
    var down = target.closest('[data-bw-qty-down]');
    var up = target.closest('[data-bw-qty-up]');
    if (down || up) {
      var wrap = (down || up).closest('[data-bw-qty]');
      if (!wrap) return;
      var input = wrap.querySelector('[data-bw-qty-input]');
      if (!input) return;
      var value = parseInt(input.value, 10);
      if (isNaN(value)) value = 1;
      value = up ? value + 1 : value - 1;
      if (value < 1) value = 1;
      input.value = String(value);
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // --- Accordions ---
    var accToggle = target.closest('[data-bw-accordion-toggle]');
    if (accToggle) {
      var item = accToggle.parentNode;
      var body = item && item.querySelector('[data-bw-accordion-body]');
      var open = accToggle.getAttribute('aria-expanded') === 'true';
      accToggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      if (body) body.hidden = open;
    }
  });

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    // Choosing a delivery frequency implies the subscribe option, and the
    // radio carries the selling_plan id that actually gets posted.
    if (target.matches('[data-bw-plan-select]')) {
      var form = target.closest('form');
      if (!form) return;
      var subscribe = form.querySelector('[data-bw-plan-subscribe]');
      if (subscribe) {
        subscribe.value = target.value;
        subscribe.checked = true;
      }
    }
  });
})();

/**
 * Reviews carousel — endless in both directions.
 *
 * The same trick the homepage sliders use, because a second approach to the
 * same problem is a second set of bugs: the whole set is cloned either side of
 * the originals, the viewer starts on the middle set, and whenever they drift
 * into a clone the scroll position jumps back by exactly one set width with
 * smooth scrolling switched off. The jump is invisible because the content
 * either side of it is identical, so there is no rewind to see.
 *
 * Both clone sets are built in FORWARD order. Reversing the leading one would
 * put the first card next to its own copy at the junction and make the row run
 * backwards when scrolled left; the sequence has to read 1 2 3 | 1 2 3 | 1 2 3
 * for every junction to continue cleanly.
 *
 * Normalising happens only once scrolling settles, so it never fights a drag
 * or a smooth-scrolled button press that is still in flight.
 */
(function () {
  'use strict';

  var REAL = '.bw-reviews__item:not([data-bw-clone])';

  function reals(track) {
    return track.querySelectorAll(REAL);
  }

  function step(track) {
    var item = track.querySelector('.bw-reviews__item');
    if (!item) return track.clientWidth;
    var styles = window.getComputedStyle(track);
    var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return item.getBoundingClientRect().width + gap;
  }

  function setWidth(track) {
    return reals(track).length * step(track);
  }

  // Move without animating, so the rewind cannot be seen.
  function jumpTo(track, left) {
    var previous = track.style.scrollBehavior;
    track.style.scrollBehavior = 'auto';
    track.scrollLeft = left;
    void track.offsetWidth;
    track.style.scrollBehavior = previous || '';
  }

  function isLooped(track) {
    return !!track.querySelector('[data-bw-clone]');
  }

  function buildLoop(track) {
    if (isLooped(track)) return;
    var originals = Array.prototype.slice.call(reals(track));
    // One card has nothing to loop between.
    if (originals.length < 2) return;

    var before = document.createDocumentFragment();
    var after = document.createDocumentFragment();

    for (var i = 0; i < originals.length; i++) {
      var head = originals[i].cloneNode(true);
      var tail = originals[i].cloneNode(true);
      [head, tail].forEach(function (clone) {
        clone.setAttribute('data-bw-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        // Clones repeat real content; keep them out of the tab order so a
        // keyboard user does not walk the same quotes three times.
        var focusable = clone.querySelectorAll('a, button, input, select, textarea');
        for (var k = 0; k < focusable.length; k++) {
          focusable[k].setAttribute('tabindex', '-1');
        }
      });
      before.appendChild(head);
      after.appendChild(tail);
    }

    track.insertBefore(before, track.firstChild);
    track.appendChild(after);

    // Land on the first real card once layout has settled enough to measure.
    window.requestAnimationFrame(function () {
      jumpTo(track, setWidth(track));
    });
  }

  // Keep the viewer inside the middle set.
  function normalize(track) {
    if (!isLooped(track)) return;
    var width = setWidth(track);
    if (width <= 0) return;
    var left = track.scrollLeft;
    if (left < width * 0.5) jumpTo(track, left + width);
    else if (left >= width * 1.5) jumpTo(track, left - width);
  }

  function syncButtons(track) {
    var section = track.closest('.bw-reviews');
    if (!section) return;
    var prev = section.querySelector('[data-bw-reviews-prev]');
    var next = section.querySelector('[data-bw-reviews-next]');
    // Looped, there is no end to be at. Unlooped means a single card, where
    // neither direction goes anywhere.
    var dead = !isLooped(track);
    if (prev) prev.disabled = dead;
    if (next) next.disabled = dead;
  }

  function trackFor(button) {
    var section = button.closest('.bw-reviews');
    return section ? section.querySelector('[data-bw-reviews-track]') : null;
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var prev = target.closest('[data-bw-reviews-prev]');
    var next = target.closest('[data-bw-reviews-next]');
    if (!prev && !next) return;

    var track = trackFor(prev || next);
    if (!track) return;
    track.scrollLeft += next ? step(track) : -step(track);
  });

  document.addEventListener('scroll', function (event) {
    var target = event.target;
    if (!(target instanceof Element) || !target.matches('[data-bw-reviews-track]')) return;
    // Settle first. Jumping mid-gesture would fight the drag, and mid-smooth
    // -scroll would cancel the animation the button just started.
    window.clearTimeout(target._bwReviewsSettle);
    target._bwReviewsSettle = window.setTimeout(function () {
      normalize(target);
    }, 90);
  }, true);

  function init() {
    var tracks = document.querySelectorAll('[data-bw-reviews-track]');
    for (var i = 0; i < tracks.length; i++) {
      buildLoop(tracks[i]);
      syncButtons(tracks[i]);
    }
  }

  // Web fonts change the card widths, and with them one set's width — so the
  // landing position is worth re-taking once they have settled, but only while
  // the viewer is still on it.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      var tracks = document.querySelectorAll('[data-bw-reviews-track]');
      for (var i = 0; i < tracks.length; i++) {
        if (isLooped(tracks[i]) && tracks[i].scrollLeft === 0) jumpTo(tracks[i], setWidth(tracks[i]));
      }
    });
  }

  document.addEventListener('shopify:section:load', init);
  init();
})();

/**
 * Mobile filter sheet.
 *
 * The rail and the sheet are the same element — below 990px CSS turns it into
 * a bottom sheet — so there is only ever one copy of the filter form and no
 * duplicate field names to reconcile.
 */
(function () {
  'use strict';

  var LOCK_CLASS = 'bw-scroll-locked';

  function parts(el) {
    var section = el.closest('.bw-collection');
    if (!section) return null;
    return {
      rail: section.querySelector('[data-bw-filter-rail]'),
      scrim: section.querySelector('[data-bw-filter-scrim]'),
      trigger: section.querySelector('[data-bw-filter-open]')
    };
  }

  function open(p) {
    if (!p || !p.rail) return;
    if (p.scrim) {
      p.scrim.hidden = false;
      // Next frame, so the transition has a start value to animate from.
      requestAnimationFrame(function () { p.scrim.classList.add('is-open'); });
    }
    requestAnimationFrame(function () { p.rail.classList.add('is-open'); });
    if (p.trigger) p.trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add(LOCK_CLASS);

    var first = p.rail.querySelector('[data-bw-filter-close]');
    if (first) first.focus();
  }

  function close(p) {
    if (!p || !p.rail) return;
    p.rail.classList.remove('is-open');
    if (p.scrim) {
      p.scrim.classList.remove('is-open');
      window.setTimeout(function () { p.scrim.hidden = true; }, 280);
    }
    if (p.trigger) {
      p.trigger.setAttribute('aria-expanded', 'false');
      p.trigger.focus();
    }
    document.body.classList.remove(LOCK_CLASS);
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var opener = target.closest('[data-bw-filter-open]');
    if (opener) { open(parts(opener)); return; }

    var closer = target.closest('[data-bw-filter-close]');
    if (closer) { close(parts(closer)); return; }

    var scrim = target.closest('[data-bw-filter-scrim]');
    if (scrim) { close(parts(scrim)); }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var openRail = document.querySelector('[data-bw-filter-rail].is-open');
    if (openRail) close(parts(openRail));
  });

  // If the viewport grows past the breakpoint while the sheet is open, the
  // rail becomes a static column again and the scroll lock would be stranded.
  var wide = window.matchMedia('(min-width: 990px)');
  var onChange = function (e) {
    if (!e.matches) return;
    var openRail = document.querySelector('[data-bw-filter-rail].is-open');
    if (openRail) close(parts(openRail));
    document.body.classList.remove(LOCK_CLASS);
  };
  if (wide.addEventListener) wide.addEventListener('change', onChange);
  else if (wide.addListener) wide.addListener(onChange);

  // The filtering layer asks the sheet to step aside once new results are in.
  document.addEventListener('bw:filters-applied', function (event) {
    var section = event.detail && event.detail.section;
    if (!section) return;
    var openRail = section.querySelector('[data-bw-filter-rail].is-open');
    if (openRail) close(parts(openRail));
  });
})();

/**
 * Filtering without a page load, via Shopify's Section Rendering API.
 *
 * Requesting <collection>?<filters>&section_id=<id> returns just this
 * section's HTML. Everything is still rendered by Shopify, so Wholesale Hero
 * pricing, Judge.me rating metafields and the paginate loop behave exactly as
 * they do on a full request — none of that is reimplemented here.
 *
 * Progressive enhancement: the filter form remains a real GET form. If any of
 * the APIs below are missing, or a request fails, this falls back to a normal
 * navigation rather than leaving stale results on screen.
 */
(function () {
  'use strict';

  var SUPPORTED =
    'fetch' in window &&
    'URLSearchParams' in window &&
    'DOMParser' in window &&
    !!(window.history && window.history.pushState);

  function sectionOf(el) {
    return el.closest('[data-bw-collection]');
  }

  function urlFromForm(section) {
    var base = section.getAttribute('data-collection-url') || window.location.pathname;
    var form = section.querySelector('[data-bw-filter-form]');
    if (!form) return base;
    var clean = new URLSearchParams();
    new URLSearchParams(new FormData(form)).forEach(function (value, key) {
      if (value !== '') clean.append(key, value);
    });
    var query = clean.toString();
    return query ? base + '?' + query : base;
  }

  // Which filter groups the shopper had open, so re-rendering the rail does
  // not silently collapse everything under them.
  function readOpenState(root) {
    var state = [];
    var toggles = root.querySelectorAll('[data-bw-filter-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      state.push(toggles[i].getAttribute('aria-expanded') === 'true');
    }
    return state;
  }

  function restoreOpenState(root, state) {
    var toggles = root.querySelectorAll('[data-bw-filter-toggle]');
    for (var i = 0; i < toggles.length && i < state.length; i++) {
      var expanded = state[i];
      toggles[i].setAttribute('aria-expanded', expanded ? 'true' : 'false');
      var group = toggles[i].closest('[data-bw-filter-group]');
      var body = group && group.querySelector('[data-bw-filter-body]');
      if (body) body.hidden = !expanded;
    }
  }

  function apply(section, url, push) {
    var results = section.querySelector('[data-bw-results-region]');
    var filters = section.querySelector('[data-bw-filters-region]');
    var sectionId = section.getAttribute('data-section-id');

    if (!SUPPORTED || !results || !sectionId) {
      window.location.assign(url);
      return Promise.resolve(false);
    }

    // Clicking quickly fires overlapping requests; without this the slower
    // one can land last and show the wrong results.
    if (section._bwAbort) section._bwAbort.abort();
    var controller = ('AbortController' in window) ? new AbortController() : null;
    section._bwAbort = controller;

    results.setAttribute('aria-busy', 'true');
    results.classList.add('is-loading');

    var separator = url.indexOf('?') === -1 ? '?' : '&';
    var endpoint = url + separator + 'section_id=' + encodeURIComponent(sectionId);

    return fetch(endpoint, { signal: controller ? controller.signal : undefined })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var nextResults = doc.querySelector('[data-bw-results-region]');
        var nextFilters = doc.querySelector('[data-bw-filters-region]');
        if (!nextResults) throw new Error('no results region in response');

        results.innerHTML = nextResults.innerHTML;

        if (nextFilters && filters) {
          var open = readOpenState(filters);
          // The mobile sheet scrolls on this element. Replacing its contents
          // collapses the scroll height for a moment, which drops the position
          // back to the top and throws the shopper to the first group after
          // every tick.
          var scrolled = filters.scrollTop;
          filters.innerHTML = nextFilters.innerHTML;
          restoreOpenState(filters, open);
          filters.scrollTop = scrolled;
        }

        if (push) window.history.pushState({ bwCollection: true }, '', url);
        results.setAttribute('aria-busy', 'false');
        results.classList.remove('is-loading');
        return true;
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return false;
        // Rather than strand the shopper on stale results, do the navigation
        // the form would have done anyway.
        window.location.assign(url);
        return false;
      });
  }

  // Desktop: apply on change. Mobile batches instead, and submits via the
  // sheet's Apply button, which lands in the submit handler below.
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.matches('.bw-filters__input')) return;
    if (!window.matchMedia('(min-width: 990px)').matches) return;
    var section = sectionOf(target);
    if (!section) return;
    apply(section, urlFromForm(section), true);
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof Element) || !form.matches('[data-bw-filter-form]')) return;
    var section = sectionOf(form);
    if (!section) return;
    event.preventDefault();
    apply(section, urlFromForm(section), true).then(function (ok) {
      if (!ok) return;
      // "Show results" should show them, so let the sheet get out of the way.
      document.dispatchEvent(new CustomEvent('bw:filters-applied', {
        detail: { section: section }
      }));
    });
  });

  // Pagination inside the swapped region. The href already carries every
  // active filter, so it is used as-is.
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    var link = target.closest('[data-bw-results-region] .bw-collection__pagination a[href]');
    if (!link) return;
    var section = sectionOf(link);
    if (!section) return;
    event.preventDefault();
    apply(section, link.getAttribute('href'), true).then(function (ok) {
      if (!ok) return;
      var main = section.querySelector('.bw-collection__main');
      if (main) main.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  // Back and forward should move through filter states, not out of the
  // collection entirely.
  window.addEventListener('popstate', function () {
    var section = document.querySelector('[data-bw-collection]');
    if (!section) return;
    apply(section, window.location.pathname + window.location.search, false);
  });
})();

/**
 * Collection tab strip — scroll indicator.
 *
 * The strip suppresses the native scrollbar, so nothing signals that the tabs
 * continue past the edge of a narrow screen. This drives the hairline under
 * it: thumb width is the visible fraction of the strip, thumb offset is the
 * scroll progress. The track is revealed only while there is something to
 * scroll, which is why no breakpoint appears here — a wide screen fits every
 * tab and the bar stays hidden on its own.
 */
(function () {
  'use strict';

  // Below this the thumb stops reading as a bar and starts reading as a dot.
  var MIN_THUMB = 24;

  function sync(nav) {
    var scroller = nav.querySelector('[data-bw-tabs-scroller]');
    var bar = nav.querySelector('[data-bw-tabs-scrollbar]');
    if (!scroller || !bar) return;

    var thumb = bar.querySelector('[data-bw-tabs-thumb]');
    if (!thumb) return;

    var visible = scroller.clientWidth;
    var total = scroller.scrollWidth;
    var overflow = total - visible;

    // A pixel of slack for sub-pixel rounding, which otherwise leaves the bar
    // showing over a strip that has nothing to scroll.
    if (overflow <= 1 || visible === 0) {
      bar.classList.remove('is-scrollable');
      return;
    }

    bar.classList.add('is-scrollable');

    // Measured after the class lands: the track has no width while hidden.
    var track = bar.clientWidth;
    if (track === 0) return;

    var width = Math.max(MIN_THUMB, Math.round(track * visible / total));
    // Clamped because iOS rubber-banding reports scrollLeft outside the range.
    var progress = Math.min(1, Math.max(0, scroller.scrollLeft / overflow));

    thumb.style.width = width + 'px';
    thumb.style.left = Math.round((track - width) * progress) + 'px';
  }

  function syncAll() {
    var navs = document.querySelectorAll('[data-bw-tabs]');
    for (var i = 0; i < navs.length; i++) sync(navs[i]);
  }

  // Scroll fires far more often than the thumb can usefully move, so coalesce
  // to one measurement per frame.
  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      syncAll();
    });
  }

  // Capture phase: scroll does not bubble, so a delegated listener only sees
  // it on the way down.
  document.addEventListener('scroll', function (event) {
    var target = event.target;
    if (target instanceof Element && target.hasAttribute('data-bw-tabs-scroller')) {
      schedule();
    }
  }, true);

  window.addEventListener('resize', schedule);
  // Web fonts change every tab's width, and with it whether the strip
  // overflows at all.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(syncAll);
  }
  // Re-render in the theme editor replaces the markup this measured.
  document.addEventListener('shopify:section:load', syncAll);

  syncAll();
})();

/**
 * Drag to scroll.
 *
 * Every horizontal track on the site is a native scroll container, which a
 * touch screen can swipe but a mouse cannot: there is no built-in drag
 * gesture for a pointer, so on desktop and tablet the arrows were the only
 * way through. This makes press-and-drag work.
 *
 * Mouse only. Touch and pen already scroll natively, with momentum and
 * rubber-banding that a scrollLeft loop cannot reproduce, so hijacking them
 * would make those devices worse.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-bw-slider-track], [data-bw-reviews-track], [data-bw-tabs-scroller]';

  // How far the pointer travels before a press counts as a drag rather than a
  // click. Too low and a click on a product card scrolls the row a few pixels
  // and is swallowed; too high and the track feels stuck before it moves.
  var THRESHOLD = 4;

  var active = null;
  var suppressClick = false;

  function scrollable(track) {
    return track.scrollWidth - track.clientWidth > 1;
  }

  // The grab cursor should only appear where there is something to drag. A
  // feature pair is a grid on desktop and a scroller on mobile; the tab strip
  // only overflows once the tabs stop fitting.
  function markDraggable() {
    var list = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < list.length; i++) {
      list[i].classList.toggle('bw-draggable', scrollable(list[i]));
    }
  }

  document.addEventListener('pointerdown', function (event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (!(event.target instanceof Element)) return;

    var track = event.target.closest(SELECTOR);
    if (!track || !scrollable(track)) return;

    active = {
      track: track,
      id: event.pointerId,
      // startX only decides when the press becomes a drag. The scrolling
      // itself works off lastX, one frame at a time — see pointermove.
      startX: event.clientX,
      lastX: event.clientX,
      moved: false
    };
  });

  document.addEventListener('pointermove', function (event) {
    if (!active || event.pointerId !== active.id) return;

    var dx = event.clientX - active.startX;

    if (!active.moved) {
      if (Math.abs(dx) < THRESHOLD) return;
      active.moved = true;
      // Suspends smooth scrolling and snapping — see the DRAG TO SCROLL block
      // in bw-components.css for why both would otherwise cancel the drag.
      active.track.classList.add('is-dragging');
      // Keeps the drag alive when the pointer leaves the track, so it does not
      // die halfway across a wide swipe.
      try { active.track.setPointerCapture(active.id); } catch (e) {}
    }

    event.preventDefault();

    // Applied as a per-frame delta rather than an absolute offset from where
    // the drag began. The looping slider rewrites scrollLeft behind our back
    // when it rewinds into a clone set — normally only once scrolling settles,
    // but a drag held still for 90ms counts as settled. Against an absolute
    // offset that rewind would be yanked straight back and the seam would
    // show; against a delta the drag simply carries on from wherever the
    // track now is.
    active.track.scrollLeft += active.lastX - event.clientX;
    active.lastX = event.clientX;
  }, { passive: false });

  function release(event) {
    if (!active) return;
    if (event && event.pointerId !== undefined && event.pointerId !== active.id) return;

    var finished = active;
    active = null;

    try { finished.track.releasePointerCapture(finished.id); } catch (e) {}
    if (!finished.moved) return;

    // Snapping comes back with the class, and the track settles on the nearest
    // slide from wherever the drag left it.
    finished.track.classList.remove('is-dragging');

    // A drag that happens to end over a product card must not open it. The
    // flag clears on the next task, by which time the click has been and gone.
    suppressClick = true;
    window.setTimeout(function () { suppressClick = false; }, 0);
  }

  document.addEventListener('pointerup', release);
  document.addEventListener('pointercancel', release);

  document.addEventListener('click', function (event) {
    if (!suppressClick) return;
    event.preventDefault();
    event.stopPropagation();
  }, true);

  // Images and links are natively draggable, and that gesture would take over
  // from this one, leaving a ghost image stuck to the cursor.
  document.addEventListener('dragstart', function (event) {
    if (active && active.moved) event.preventDefault();
  });

  window.addEventListener('resize', markDraggable);
  document.addEventListener('shopify:section:load', markDraggable);
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(markDraggable);
  }

  markDraggable();
})();

/**
 * Product page — the Add to cart price follows the quantity.
 *
 * The markup has carried a [data-bw-add-price] hook since the page was built,
 * but nothing ever wrote to it: the button has always shown the unit price,
 * whatever the quantity said. This wires it up.
 *
 * The unit price rides on the element as data-bw-unit-price, in cents, so the
 * arithmetic never has to parse a formatted string back into a number — that
 * breaks the moment a shop uses a comma decimal or a currency suffix.
 */
(function () {
  'use strict';

  function formatMoney(cents) {
    var t = window.theme;
    // Debut's own helper, so the shop's money format is respected rather than
    // a dollar sign being assumed.
    if (t && t.Currency && typeof t.Currency.formatMoney === 'function') {
      return t.Currency.formatMoney(cents, t.moneyFormat);
    }
    // No formatter: leave the server-rendered price alone rather than print
    // something in the wrong currency.
    return null;
  }

  function sync(scope) {
    var price = scope.querySelector('[data-bw-add-price]');
    var input = scope.querySelector('[data-bw-qty-input]');
    if (!price || !input) return;

    var unit = parseInt(price.getAttribute('data-bw-unit-price'), 10);
    if (isNaN(unit)) return;

    var qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 1) qty = 1;

    var text = formatMoney(unit * qty);
    if (text !== null) price.textContent = text;
  }

  function scopeOf(el) {
    return el.closest('.bw-product') || document;
  }

  // The stepper dispatches change; typing in the field fires input.
  ['change', 'input'].forEach(function (evt) {
    document.addEventListener(evt, function (event) {
      var target = event.target;
      if (target instanceof Element && target.hasAttribute('data-bw-qty-input')) {
        sync(scopeOf(target));
      }
    });
  });

  var section = document.querySelector('.bw-product');
  if (section) sync(section);
})();

/**
 * Product page — move the Appstle widget out of the buy row.
 *
 * .bw-product__buy-row is built to hold the quantity stepper and Add to cart
 * side by side. Appstle injects its widget between them, and being a full-width
 * block it splits the pair onto separate lines.
 *
 * The section already renders an empty container for it in the right place,
 * above the quantity label, so the widget is moved there. Moving the node keeps
 * the element itself — its listeners and Appstle's own references to it survive,
 * which replacing the markup would not.
 */
(function () {
  'use strict';

  var ROW = '.bw-product__buy-row';
  var MOUNT = '[data-bw-subscription-mount]';

  function relocate() {
    var mount = document.querySelector(MOUNT);
    var row = document.querySelector(ROW);
    if (!mount || !row) return;

    var widgets = row.querySelectorAll('[id^="appstle_subscription_widget"]');
    for (var i = 0; i < widgets.length; i++) {
      if (widgets[i] === mount || mount.contains(widgets[i])) continue;
      mount.appendChild(widgets[i]);
    }
  }

  // Appstle renders after this script and can re-render, so this watches the
  // row rather than running once. Moving the node fires the observer again,
  // which then finds nothing to do.
  var row = document.querySelector(ROW);
  if (row && 'MutationObserver' in window) {
    new MutationObserver(relocate).observe(row, { childList: true });
  }

  relocate();
})();

/**
 * Autoplaying decorative video — reduced motion.
 *
 * These clips are decoration: muted, looped, no controls. Someone who has
 * asked their system for reduced motion should get the poster frame instead,
 * and a browser will not do that on its own — autoplay is honoured regardless
 * of the preference, so it has to be undone here.
 *
 * In bw-theme.js rather than bw-home.js so it holds wherever such a video is
 * used, not only on the homepage.
 */
(function () {
  'use strict';

  var query = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if (!query) return;

  function apply() {
    var videos = document.querySelectorAll('[data-bw-autoplay-video]');
    for (var i = 0; i < videos.length; i++) {
      var video = videos[i];
      if (query.matches) {
        video.removeAttribute('autoplay');
        video.pause();
        // Back to the first frame, which is the poster the card was given.
        try { video.currentTime = 0; } catch (e) {}
      } else if (video.paused) {
        var playing = video.play();
        // Autoplay can still be refused; there is nothing to do about it.
        if (playing && typeof playing.catch === 'function') playing.catch(function () {});
      }
    }
  }

  // The preference can change while the page is open.
  if (typeof query.addEventListener === 'function') query.addEventListener('change', apply);
  else if (typeof query.addListener === 'function') query.addListener(apply);

  document.addEventListener('shopify:section:load', apply);
  apply();
})();
